import { AppStreamSDK } from "@utils/AppStreamSDK";
import { useCallback, useEffect, useState } from "react";
import { EmbedConstants as AppStreamEmbedConstant } from "@amzn/photon-portal-embed-sdk";
import { SessionState } from "@customTypes/appStream";
import { useSessionDataStore } from "@hooks/useSessionDataStore";
import { useOnStarted } from "./useOnStarted";
import { useGetLogger } from "@hooks/useGetLogger";

const useOnEnded = (appStreamSdk: AppStreamSDK) => {
    const log = useGetLogger();

    return useCallback(async () => {
        log.logMessage("User ended the session. Signing out.");
    }, [appStreamSdk]);
};

const useOnDisconnected = (appStreamSdk: AppStreamSDK) => {
    const log = useGetLogger();

    return useCallback(async () => {
        log.logMessage("User disconnected the session.");
    }, [appStreamSdk]);
};

const NO_SESSION_STATE: SessionState = {
    sessionStatus: "Unknown",
    sessionId: undefined,
};

export const useSessionStatusListener = (appStreamSdk: AppStreamSDK) => {
    const [sessionState, setSessionState] = useState<SessionState>();
    const setSessionStatus = useSessionDataStore(
        (store) => store.setSessionStatus
    );
    const setSessionID = useSessionDataStore((store) => store.setSessionID);
    const log = useGetLogger();

    useEffect(() => {
        if (appStreamSdk == null) {
            setSessionState(NO_SESSION_STATE);
            return;
        }

        const listener = (event: SessionState) => {
            if (event) {
                log.logMessage(
                    `AS2 embed SESSION_STATE_CHANGE event: ${JSON.stringify(event)}`,
                    {
                        SessionState: event.sessionStatus,
                        EventSessionId: event.sessionId,
                    }
                );
                setSessionState(event);
                setSessionStatus(event.sessionStatus);
            }
        };
        appStreamSdk.addEventListener(
            AppStreamEmbedConstant.Events.EVENT_SESSION_STATE_CHANGED,
            listener
        );

        return () => {
            appStreamSdk.removeEventListener(
                AppStreamEmbedConstant.Events.EVENT_SESSION_STATE_CHANGED,
                listener
            );
        };
    }, [appStreamSdk]);

    const onStarted = useOnStarted(appStreamSdk);
    const onEnded = useOnEnded(appStreamSdk);
    const onDisconnected = useOnDisconnected(appStreamSdk);

    useEffect(() => {
        if (appStreamSdk == null || !sessionState) {
            return;
        }

        switch (sessionState.sessionStatus) {
            case "Started":
                void onStarted();
                return;
            case "Ended":
                void onEnded();
                return;
            case "Disconnected":
                void onDisconnected();
        }
    }, [appStreamSdk, sessionState?.sessionStatus]);

    useEffect(() => {
        if (sessionState?.sessionStatus === "Started") {
            setSessionID(sessionState.sessionId);
        }
    }, [sessionState?.sessionId]);
};
